<template>
  <div class="stats_card">
    <b-card no-body header-tag="header">
      <template v-slot:header>
        <b-card-title>{{ title }}</b-card-title>
        <b-card-sub-title>{{ subTitle }}</b-card-sub-title>
      </template>
      <b-tabs card>
        <b-tab v-if="stats" title="Basic Stats" active>
          <b-list-group horizontal class="mb-1">
            <b-list-group-item>
              <strong>Requests</strong>: {{ stats.requests.toLocaleString() }}
            </b-list-group-item>
            <b-list-group-item><strong>Bandwidth</strong>: {{ bandwidth }}</b-list-group-item>
          </b-list-group>

          <b-list-group horizontal class="mb-1">
            <b-list-group-item v-b-tooltip.hover title='"Success" status codes'>
              <strong>2xx Requests</strong>: {{ stats.status2xx.toLocaleString() }}
            </b-list-group-item>
            <b-list-group-item v-b-tooltip.hover title='"Redirection" status codes'>
              <strong>3xx Requests</strong>: {{ stats.status3xx.toLocaleString() }}
            </b-list-group-item>
            <b-list-group-item v-b-tooltip.hover title='"Client Error" status codes'>
              <strong>4xx Requests</strong>: {{ stats.status4xx.toLocaleString() }}
            </b-list-group-item>
            <b-list-group-item v-b-tooltip.hover title='"Server Error" status codes'>
              <strong>5xx Requests</strong>: {{ stats.status5xx.toLocaleString() }}
            </b-list-group-item>
          </b-list-group>

          <div v-if="stats && 'ipstats_details' in stats">
            <b-list-group>
              <b-list-group-item>
                <h5>Top 5 IPs</h5>
                <b-table-lite
                  v-if="stats && 'ipstats_details' in stats"
                  :items="topIpTableItems"
                  class="mb-1"
                ></b-table-lite>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-spinner v-else variant="success" label="spinning" class="mt-1"></b-spinner>
        </b-tab>

        <b-tab v-if="stats && stats.imgopto > 0" title="Image Optimizer Stats">
          <b-list-group horizontal class="mb-1">
            <b-list-group-item>
              <strong>Image Optimizer Requests</strong>: {{ stats.imgopto.toLocaleString() }}
            </b-list-group-item>
          </b-list-group>

          <b-alert show variant="info">
            Please note IO stats broken down by domain (below) are unavailable prior to August 5,
            2020.
          </b-alert>
          <h5>Bandwidth Statistics</h5>
          <h6 class="text-muted">
            Compares the cumulative size of all images before and after optimization.
          </h6>
          <div v-if="stats && 'imgopto_details' in stats">
            <b-table-lite
              v-if="stats && 'imgopto_details' in stats"
              :items="detailsTableItems"
              class="mb-1"
            ></b-table-lite>
          </div>
          <b-spinner v-else variant="success" label="spinning" class="m-1"></b-spinner>

          <h5>HIT/MISS Data</h5>
          <h6 class="text-muted">
            HIT: optimized image found in the cache and served directly from AGCDN; MISS: optimized
            image not found and had to be requested from Pantheon and optimized. Cached optimized
            images do not count towards monthly billing.
          </h6>
          <div v-if="stats && 'imgopto_details' in stats">
            <b-table-lite
              v-if="stats && 'imgopto_details' in stats"
              :items="hitMissTableItems"
              class="mb-1"
            ></b-table-lite>
          </div>
          <b-spinner v-else variant="success" label="spinning" class="m-1"></b-spinner>
        </b-tab>

        <b-tab v-if="stats && hasWaf" title="WAF Stats">
          <b-alert show variant="info">
            Please note WAF stats broken down by domain (below) are unavailable prior to July 10,
            2020.
          </b-alert>
          <b-table-lite
            v-if="stats && 'waf_details' in stats"
            :items="wafDetailsTableItems"
            class="mb-1"
          ></b-table-lite>
          <b-spinner v-else variant="success" label="spinning" class="m-1"></b-spinner>
        </b-tab>

        <b-tab v-if="stats && hasBlockLog" title="Block Log">
          <b-alert show variant="info">
            Please note that Blocked Traffic logs are unavailable prior to Sept. 3, 2020.
          </b-alert>
          <b-form-group label="Group by...">
            <b-form-radio-group
              v-model="groupBy"
              :options="groupByOptions"
              name="groupBy"
            ></b-form-radio-group>
          </b-form-group>
          <b-table-lite
            v-if="stats && 'blocked_details' in stats"
            :items="blockLogTableItems"
            class="mb-1"
          ></b-table-lite>
          <b-spinner v-else variant="success" label="spinning" class="m-1"></b-spinner>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'StatsCard',
  props: {
    stats: {
      type: Object,
      default: null,
    },
    serviceId: {
      type: String,
      default: '',
    },
    period: {
      type: Number,
      default: 30,
    },
    hasWaf: {
      type: Boolean,
      default: false,
    },
    hasIo: {
      type: Boolean,
      default: false,
    },
    hasBlockLog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      groupBy: 'geo_country',
      groupByOptions: [
        { text: 'IP', value: 'ip' },
        { text: 'Geo (Country-level)', value: 'geo_country' },
        { text: 'Geo (Region-level)', value: 'geo_region' },
      ],
    };
  },
  computed: {
    title() {
      if (this.period) {
        return `${this.period}-day Summary`;
      }
      return '';
    },
    subTitle() {
      if (this.stats) {
        return `${this.stats.from} to ${this.stats.to}`;
      }
      return '';
    },
    bandwidth() {
      if (!this.stats) {
        return '';
      }
      return this.bandwidthFormatted(this.stats.bandwidth);
    },
    detailsTableItems() {
      if (!this.stats) {
        return [];
      }
      if (!this.stats.imgopto_details) {
        return [];
      }
      const hosts = [...new Set(this.stats.imgopto_details.map(d => d.req_h_host))];
      return hosts.map(host => {
        const counts = this.stats.imgopto_details.filter(d => d.req_h_host === host);
        const ifsz = counts.reduce((total, item) => total + parseInt(item.sum_ifsz, 10), 0);
        const ofsz = counts.reduce((total, item) => total + parseInt(item.sum_ofsz, 10), 0);
        return {
          host,
          total_in_filesize: this.bandwidthFormatted(ifsz),
          total_out_filesize: this.bandwidthFormatted(ofsz),
          ratio: `${this.compressionRatio(ifsz, ofsz)} %`,
        };
      });
    },
    topIpTableItems() {
      if (!this.stats) return [];
      if (!this.stats.ipstats_details) return [];
      return this.stats.ipstats_details.map(ipRow => ({
        hits: parseInt(ipRow.hits, 10).toLocaleString(),
        'IP Address': ipRow.ip_address,
        country: ipRow.country,
        region: ipRow.region,
        city: ipRow.city.replace(
          /\w\S*/g,
          t => `${t.charAt(0).toUpperCase()}${t.substr(1).toLowerCase()}`
        ),
      }));
    },
    hitMissTableItems() {
      if (!this.stats) {
        return [];
      }
      if (!this.stats.imgopto_details) {
        return [];
      }
      const hosts = [...new Set(this.stats.imgopto_details.map(d => d.req_h_host))];
      return hosts.map(host => {
        const counts = this.stats.imgopto_details.filter(d => d.req_h_host === host);
        const hits = counts.filter(d => d.cache_status === 'HIT');
        const misses = counts.filter(d => d.cache_status !== 'HIT');
        return {
          host,
          hit_count: hits.length > 0 ? parseInt(hits[0].io_count, 10).toLocaleString() : '0',
          miss_count: misses.length > 0 ? parseInt(misses[0].io_count, 10).toLocaleString() : '0',
        };
      });
    },
    wafDetailsTableItems() {
      if (!this.stats) {
        return [];
      }
      if (!this.stats.waf_details) {
        return [];
      }
      return Object.keys(this.stats.waf_details).map(domain => ({
        domain,
        requests_passed: this.stats.waf_details[domain].pass_total.toLocaleString(),
        requests_logged: this.stats.waf_details[domain].logged_total.toLocaleString(),
        requests_blocked: this.stats.waf_details[domain].blocked_total.toLocaleString(),
      }));
    },
    blockLogTableItems() {
      if (!this.stats) {
        return [];
      }
      if (!this.stats.blocked_details) {
        return [];
      }

      if (this.groupBy === 'ip') {
        return this.stats.blocked_details.map(d => ({
          host: d.req_h_host,
          IP: d.client_ip,
          'IP Count': d.client_ip_count,
        }));
      }

      if (this.groupBy === 'geo_country') {
        return this.stats.blocked_details.map(d => ({
          host: d.req_h_host,
          country_code: d.country_code,
          blocked_count: d.country_code_count,
        }));
      }

      return this.stats.blocked_details.map(d => ({
        host: d.req_h_host,
        region: d.region,
        region_count: d.region_count,
      }));
    },
  },
  watch: {
    // eslint-disable-next-line
    groupBy: function (newVal, oldVal) {
      this.$emit('group-by-change', { newVal, period: this.period });
    },
  },
  methods: {
    bandwidthFormatted(v) {
      const availUnits = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb'];
      let val = v;
      let unit = availUnits.shift();
      while (val >= 1024) {
        val /= 1024;
        unit = availUnits.shift();
      }
      return `${parseFloat(val.toFixed(2)).toLocaleString()} ${unit}`;
    },
    compressionRatio(bytesIn, bytesOut) {
      let bIn = bytesIn;
      if (typeof bytesIn === 'string') {
        bIn = parseInt(bytesIn, 10);
      }
      let bOut = bytesOut;
      if (typeof bytesOut === 'string') {
        bOut = parseInt(bytesOut, 10);
      }
      return (100 * ((bIn - bOut) / bIn)).toFixed(2);
    },
  },
};
</script>
